import {
  SESSION_CREATE,
  SESSION_LOGOUT,
  SESSION_GET_DATA_START,
  SESSION_GET_DATA_FINISH,
  SESSION_ACTIVATE_ACCOUNT,
  SESSION_ENABLE_ACCOUNT_ACTIVATION,
  SESSION_CLEAR_ERROR,
  SET_EULA_EXPIRED
} from '../constants/actionTypes';
import { refreshToken, getUserSelf, getBots, getConfiguration, activate, logoutRequest } from '../../api';
import { setUser } from './user.actions';
import { setRobots, setRobotConfig, setActiveRobot } from './robots.actions';
import { fetchSettings } from '../../../robotSettings/redux/reducer';
import moment from 'moment';
import { setProfile } from '../../../generalSettings/UserProfile/redux/reducer';

export const createSession = session => ({ type: SESSION_CREATE, session });

export const logoutSession = (payload) => {
  localStorage.removeItem('Tinybots.session');
  const error = !!payload && !!payload.error
  const eulaExpired = payload?.eulaExpired
  return { type: SESSION_LOGOUT, payload: { error, eulaExpired } };
};

export const logout = (payload) => async dispatch => {
  // Ask the backend to invalidate the session cookies so the next login
  // does not reuse the previous cookie. Best-effort: clear locally regardless.
  try {
    await logoutRequest();
  } catch (e) {
    console.error('Logout request failed:', e?.response?.status);
  }
  dispatch(logoutSession(payload));
};

export const clearSessionError = () => ({ type: SESSION_CLEAR_ERROR });

export const startGetSessionData = () => ({ type: SESSION_GET_DATA_START });

export const finishGetSessionData = () => ({ type: SESSION_GET_DATA_FINISH });

export const activateAccount = () => ({ type: SESSION_ACTIVATE_ACCOUNT });

export const enableAccountActivation = () => ({ type: SESSION_ENABLE_ACCOUNT_ACTIVATION });

export const setEulaExpired = (payload) => ({ type: SET_EULA_EXPIRED, payload });

export const activateEmail = data => async dispatch => {
  dispatch(activateAccount());
  await activate(data);
  setTimeout(() => dispatch(enableAccountActivation()), 30000);
};

// Pick the active robot: prefer a serial from the code-grant flow, then the
// robot saved in localStorage, otherwise let the caller fall back to the first.
const resolveActiveRobotId = robots => {
  const savedRobotId = localStorage.getItem('activeRobot');
  const codeGrantSerial = localStorage.getItem('codeGrantSerial');
  let activeRobotId = robots.find(r => r.id === parseInt(savedRobotId))?.id;

  if (codeGrantSerial) {
    const matchedRobot = robots.find(r => r.cpuSerial === codeGrantSerial) ||
                         robots.find(r => r.robot === codeGrantSerial);
    if (matchedRobot) {
      activeRobotId = matchedRobot.id;
      localStorage.setItem('activeRobot', activeRobotId);
    }
    localStorage.removeItem('codeGrantSerial');
  }

  return activeRobotId;
};

// Guard so the various callers (App init, PrivateRoute, code-grant, invites,
// heartbeat) can never trigger overlapping loads. While a load is in flight we
// return the same promise, so concurrent callers await the real completion
// instead of resolving early (which would let App mark itself initialized
// before the session is verified, flashing the login page on refresh).
let sessionDataRequest = null;

export const getSessionData = () => async dispatch => {
  if (sessionDataRequest) return sessionDataRequest;

  dispatch(startGetSessionData());

  const load = async () => {
    try {
      const user = await getUserSelf();
      // TODO remove get robots retry, temporary bugfix
      const timeoutPromise = new Promise(resolve => setTimeout(resolve, 3000))
      const robots = await getBots().catch(() => timeoutPromise.then(() => getBots()))
        .catch(() => timeoutPromise.then(() => getBots()))
        .catch(() => getBots())

      if (robots.data.length) {
        const activeRobotId = resolveActiveRobotId(robots.data);
        const fallbackRobotId = activeRobotId ?? robots.data[0].id;

        const robotConfig = await getConfiguration(fallbackRobotId);
        dispatch(fetchSettings(fallbackRobotId))
        if (!activeRobotId) {
          dispatch(setActiveRobot(robots.data[0].id))
        }
        dispatch(setRobotConfig(robotConfig.data));
      }

      const { status, id, username, mfaEnabledAt, firstName, lastName, phoneNumber, eulaVersionId, eulaUpToDate } = user.data;
      dispatch(setUser({ status, userId: id, email: username, mfaEnabledAt }));
      dispatch(setProfile({ firstName, lastName, phoneNumber: `+${phoneNumber}`, eulaVersionId, eulaUpToDate }))
      dispatch(setRobots(robots.data));

      // Cookie-only session: /self succeeded, so we are authenticated.
      dispatch(createSession({
        isAuthenticated: true,
        token_creation_time: moment().format(),
        internal_date: moment().format()
      }));
      dispatch(finishGetSessionData());
    } catch (e) {
      // 401 just means there is no valid session (e.g. not logged in yet) — clear
      // locally and send the user to login without flagging a hard error, and
      // without a needless backend /logout call. Anything else is a real failure.
      const status = e?.response?.status;
      console.error('Issue getting session data: ', status);
      dispatch(logoutSession({ error: status !== 401 }));
    } finally {
      sessionDataRequest = null;
    }
  };

  sessionDataRequest = load();
  return sessionDataRequest;
};

export const getNewTokenAsync = () => async (dispatch, getState) => {
  try {
    dispatch(startGetSessionData());
    const { refresh_token } = getState().session;

    // Token-based fallback (code-grant): send refreshToken in body
    // Cookie-based refresh: no body, backend sets cookies
    const refreshTokenResponse = refresh_token
      ? await refreshToken({ refreshToken: refresh_token })
      : await refreshToken();

    if (refresh_token && refreshTokenResponse.data && refreshTokenResponse.data.access_token) {
      // Token-based path: backend returned JSON tokens
      const session = {
        ...refreshTokenResponse.data,
        token_creation_time: moment().format(),
        internal_date: moment().format()
      };
      localStorage.setItem('Tinybots.session', JSON.stringify(session));
      dispatch(createSession(session));

      const user = await getUserSelf();
      const { status, id, username, firstName, lastName, phoneNumber, eulaVersionId, eulaUpToDate } = user.data;
      dispatch(setUser({ status, userId: id, email: username }));
      dispatch(setProfile({ firstName, lastName, phoneNumber: `+${phoneNumber}`, eulaVersionId, eulaUpToDate }));

      const bots = await getBots();
      dispatch(setRobots(bots.data));

      return Promise.resolve(session.access_token);
    } else {
      // Cookie-based path: backend set new cookies, no JSON token in response.
      // Verify the new session by calling /self.
      const user = await getUserSelf();
      const { status, id, username, mfaEnabledAt, firstName, lastName, phoneNumber, eulaVersionId, eulaUpToDate } = user.data;
      dispatch(setUser({ status, userId: id, email: username, mfaEnabledAt }));
      dispatch(setProfile({ firstName, lastName, phoneNumber: `+${phoneNumber}`, eulaVersionId, eulaUpToDate }));

      const bots = await getBots();
      dispatch(setRobots(bots.data));

      // Set cookie-only session with done: true
      const newData = {
        isAuthenticated: true,
        token_creation_time: moment().format(),
        internal_date: moment().format()
      };
      dispatch(createSession(newData));
      dispatch(finishGetSessionData());

      return Promise.resolve();
    }
  }
  catch (e) {
    if (e.response && e.response.data && e.response.data.eulaVersionUrl) {
      console.error('Eula expired');
      dispatch(logout({ error: false, eulaExpired: e.response.data.eulaVersionUrl }));
    } else {
      // Any other refresh failure (400 invalid_request, 401, network error) means
      // the refresh token is invalid or expired — treat as session expiry and logout.
      console.error('Token refresh failed, logging out:', e?.response?.status, e?.response?.data?.message);
      dispatch(logoutSession({ error: false }));
    }
    return Promise.reject(e);
  }
};
